import React, { useState, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Spinner, Dropdown } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import SubscriptionActionModal from '../../../../components/modals/SubscriptionActionModal';
import { toast } from 'react-toastify';
import adminService from '../../../../services/AdminService';
import '../../../../styles/tables.css';

const Subscriptions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [plans, setPlans] = useState([]);

  // Modal states
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [currentPage, rowsPerPage, statusFilter, planFilter]);

  // Debounced search effect
  useEffect(() => {
    if (searchQuery === '') return;

    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchSubscriptions();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchPlans = async () => {
    try {
      const result = await adminService.getPlans({ page_size: 100 });
      if (result.success) {
        setPlans(result.data.results);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (planFilter) {
        params.plan_id = planFilter;
      }

      const result = await adminService.getSubscriptions(params);

      if (result.success) {
        setApiData(result.data.results);
        setTotalRecords(result.data.count);
        setTotalPages(result.data.totalPages);
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      setError('Failed to fetch subscriptions');
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = apiData;

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePlanFilterChange = (value) => {
    setPlanFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const exportData = apiData.map(sub => ({
      ID: sub.id,
      'User Mobile': sub.user_mobile,
      'User Name': sub.user_name,
      'Plan Name': sub.plan_name,
      'Plan Price': sub.plan_price,
      Status: sub.status,
      'Start Date': sub.start_date,
      'End Date': sub.end_date,
      'Days Remaining': sub.days_remaining,
      'Auto Renew': sub.auto_renew ? 'Yes' : 'No',
      'Created At': new Date(sub.created_at).toLocaleDateString()
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscriptions');
    XLSX.writeFile(workbook, `subscriptions_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAction = (subscription, action) => {
    setSelectedSubscription(subscription);
    setActionType(action);
    setShowActionModal(true);
  };

  const handleActionSubmit = async (data) => {
    try {
      let result;
      switch (actionType) {
        case 'activate':
          result = await adminService.activateSubscription(selectedSubscription.id, {
            start_date: data.start_date
          });
          break;
        case 'cancel':
          result = await adminService.cancelSubscription(selectedSubscription.id, {
            reason: data.reason
          });
          break;
        case 'expire':
          result = await adminService.expireSubscription(selectedSubscription.id);
          break;
        case 'extend':
          result = await adminService.extendSubscription(selectedSubscription.id, {
            additional_days: data.additional_days
          });
          break;
        default:
          return { success: false, error: 'Unknown action' };
      }

      if (result.success) {
        toast.success(result.message || `Subscription ${actionType}d successfully`);
        setShowActionModal(false);
        fetchSubscriptions();
      } else {
        toast.error(result.error);
      }

      return result;
    } catch (err) {
      toast.error(`Failed to ${actionType} subscription`);
      return { success: false, error: `Failed to ${actionType} subscription` };
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'success', label: 'Active' },
      expired: { bg: 'danger', label: 'Expired' },
      cancelled: { bg: 'secondary', label: 'Cancelled' },
      pending: { bg: 'warning', label: 'Pending' }
    };
    const config = statusConfig[status?.toLowerCase()] || { bg: 'secondary', label: status };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const getDaysRemainingBadge = (days, status) => {
    if (status !== 'active') return '-';
    if (days === null || days === undefined) return '-';

    if (days <= 0) {
      return <Badge bg="danger">Expired</Badge>;
    } else if (days <= 7) {
      return <Badge bg="warning">{days} days</Badge>;
    } else if (days <= 30) {
      return <Badge bg="info">{days} days</Badge>;
    } else {
      return <Badge bg="success">{days} days</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getAvailableActions = (subscription) => {
    const actions = [];
    switch (subscription.status?.toLowerCase()) {
      case 'pending':
        actions.push({ key: 'activate', label: 'Activate', icon: 'bi-check-circle', variant: 'success' });
        actions.push({ key: 'cancel', label: 'Cancel', icon: 'bi-x-circle', variant: 'danger' });
        break;
      case 'active':
        actions.push({ key: 'extend', label: 'Extend', icon: 'bi-calendar-plus', variant: 'primary' });
        actions.push({ key: 'cancel', label: 'Cancel', icon: 'bi-x-circle', variant: 'danger' });
        actions.push({ key: 'expire', label: 'Expire Now', icon: 'bi-clock-history', variant: 'warning' });
        break;
      case 'expired':
        actions.push({ key: 'activate', label: 'Reactivate', icon: 'bi-arrow-repeat', variant: 'success' });
        break;
      case 'cancelled':
        actions.push({ key: 'activate', label: 'Reactivate', icon: 'bi-arrow-repeat', variant: 'success' });
        break;
      default:
        break;
    }
    return actions;
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#031a6f', fontWeight: '700' }}>
          <i className="fas fa-file-contract me-2"></i>
          Subscriptions
        </h2>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="bi bi-file-earmark-excel me-2"></i>
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by mobile, name..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="pending">Pending</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={planFilter}
            onChange={(e) => handlePlanFilterChange(e.target.value)}
          >
            <option value="">All Plans</option>
            {plans.map(plan => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>ID</th>
              <th>User</th>
              <th>Plan</th>
              <th>Price</th>
              <th>Status</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Days Left</th>
              <th>Auto Renew</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <div className="mt-2">Loading subscriptions...</div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-muted">
                  <i className="fas fa-inbox me-2"></i>
                  No subscriptions found
                </td>
              </tr>
            ) : (
              paginatedData.map((subscription) => (
                <tr key={subscription.id}>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-primary" size="sm" id={`dropdown-${subscription.id}`}>
                        <i className="bi bi-gear"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {getAvailableActions(subscription).map(action => (
                          <Dropdown.Item
                            key={action.key}
                            onClick={() => handleAction(subscription, action.key)}
                          >
                            <i className={`bi ${action.icon} me-2`}></i>
                            {action.label}
                          </Dropdown.Item>
                        ))}
                        {getAvailableActions(subscription).length === 0 && (
                          <Dropdown.Item disabled>No actions available</Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td><strong>{subscription.id}</strong></td>
                  <td>
                    <div>
                      <strong>{subscription.user_mobile}</strong>
                      {subscription.user_name && (
                        <div className="text-muted small">{subscription.user_name}</div>
                      )}
                    </div>
                  </td>
                  <td>{subscription.plan_name}</td>
                  <td>{formatCurrency(subscription.plan_price)}</td>
                  <td>{getStatusBadge(subscription.status)}</td>
                  <td>{subscription.start_date || '-'}</td>
                  <td>{subscription.end_date || '-'}</td>
                  <td>{getDaysRemainingBadge(subscription.days_remaining, subscription.status)}</td>
                  <td>
                    {subscription.auto_renew ? (
                      <Badge bg="info">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                  <td>{new Date(subscription.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} entries
        </div>
        <Pagination>
          <Pagination.First
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          />
          {[...Array(Math.min(5, totalPages || 1))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, (totalPages || 1) - 4 + index));
            if (pageNum > totalPages) return null;
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          />
          <Pagination.Last
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          />
        </Pagination>
      </div>

      {/* Subscription Action Modal */}
      <SubscriptionActionModal
        show={showActionModal}
        handleClose={() => setShowActionModal(false)}
        onSubmit={handleActionSubmit}
        subscription={selectedSubscription}
        actionType={actionType}
      />
    </Container>
  );
};

export default Subscriptions;
